import { useEffect, useRef } from 'react';
// eslint-disable-next-line jira/restricted/@jira+platform__services-providers-project-context
import { useProjectContext } from '@jira/platform__services-providers-project-context/src/index.tsx';
import { useQuickstart } from '@jira/software__onboarding-quickstart/src/useQuickstart.tsx';

export type Preloadable = { preload?: () => void };

/**
 * Eagerly evaluates the JSW quickstart eligibility hook and preloads the lazy
 * onboarding chunk so the API call runs in parallel with the chunk download.
 * Returns whether the onboarding component should be rendered.
 *
 * Must be imported synchronously by the layout entry — wrapping it in
 * `React.lazy` defeats the parallelism and shows the chunk as the last UFO
 * hold.
 */
export const useOnboardingPreload = (target: Preloadable): boolean => {
	const hasPreloadedRef = useRef(false);
	const projectContext = useProjectContext();
	const projectContextData = projectContext?.data;
	const canShowExperience = useQuickstart(projectContextData);

	useEffect(() => {
		if (canShowExperience && !hasPreloadedRef.current) {
			target.preload?.();
			hasPreloadedRef.current = true;
		}
	}, [canShowExperience, target]);

	return canShowExperience;
};
